import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { v4 } from 'uuid';
import { 
  createCompany, 
  updateCompany, 
  getCurrentCompany, 
  getCurrentCompanyUsers, 
  createUser,
  uploadContract,
  getContracts as getContractsApi,
  updateContract as updateContractApi,
  deleteContract,
  uploadLogo,
  getLogo as getLogoApi,
  createSetupIntent,
  getPaymentMethods as getPaymentMethodsAPI,
  getInvoices as getInvoicesApi,
  getCurrentInvoices,
  createOrUpdateSubscription,
  cancelSubscription as cancelSubscriptionApi
} from '../api';

import { useSnackbar } from 'notistack';

interface Company {
  id: string,
  name: string,
  settings: any,
  phone: string,
  email: string,
  address: string,
  owner: string,
  logo: any,
  freeProjectsRemaining: number,
  subscriptionPlan?: string,
  stripeSubscriptionId?: string,
}

interface CompanyContextType {
  company: Company | null | undefined;
  set: (company: Company) => void;
  get: () => Promise<Company | null | undefined>,
  setCurrent: () => Promise<void>,
  create: (company: any) => Promise<Company>;
  update: (company: Company) => Promise<void>;
  getCurrentUsers: () => Promise<any>;
  addUser: (user: any) => Promise<any>;

  addContract(data: any): Promise<void>;
  getContracts(): Promise<any[]>;
  updateContract: (data: any) => Promise<any>;
  removeContract(data: any): Promise<void>;
  addLogo(data: any): Promise<void>;
  getLogo(): Promise<void>;
  getSetupIntent: () => Promise<any>;
  getPaymentMethods: () => Promise<any>;
  getInvoices: () => Promise<any>;
  getNextInvoice: () => Promise<any>;
  createOrUpdateSubscription: (planId: string) => Promise<any>;
  cancelSubscription: () => Promise<any>;
}

const defaultCompanySettings = {
  projects: {
    statusColors:  [
      { name: 'Open', color: '#ffcccc', id: v4() },
      { name: 'Pre-Build', color: '#ccffcc', id: v4() },
      { name: 'Build', color: '#ccccff', id: v4() },
      { name: 'Post-Build', color: '#ffffcc', id: v4() },
      { name: 'Sent', color: '#ffccff', id: v4() },
      { name: 'Collect', color: '#ccffff', id: v4() },
      { name: 'Closed', color: '#f0f0f0', id: v4() },
    ]
  },
  materialTemplates:  [],
  supplementTemplates:  [ ],
  jobCosts:[
    {
      category: "revenue",
      editable: true,
      id: v4(),
      name: "Revenue",
      type: "fixed",
    },
    {
      category: "expense",
      editable: true,
      id: v4(),
      name: "Expenses",
      type: "fixed",
    }
  ],
  attachments: {
    groups: [
      {
        id: v4(),
        name: 'Contract', 
      },
      {
        id: v4(),
        name: 'SOL',
      },
      {
        id: v4(),
        name: 'EV',
      },
      {
        id: v4(),
        name: 'Permit',
      },
      {
        id: v4(),
        name: 'AOC',
      },
      {
        id: v4(),
        name: 'LOA',
      },
      {
        id: v4(),
        name: 'MISC',
      }
    ]
  },
  leads: {
    statusColors: [
      { name: 'Open', color: '#ffcccc', id: v4() },
      { name: 'First Follow Up', color: '#ccffcc', id: v4() },
      { name: 'Second Follow Up', color: '#ccccff', id: v4() },
      { name: 'Third Follow Up', color: '#ffffcc', id: v4() },
    ]
  }
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [company, setCompany] = useState<Company | null | undefined>(null);
  const { enqueueSnackbar } = useSnackbar();

  const set = useCallback((company: Company) => {
    setCompany(company);
  }, []);

  const get = useCallback(async () => {
    if(!company) {
      try {
        const newCompany = await getCurrentCompany();
        if(newCompany && !Array.isArray(newCompany?.settings?.projects?.statusColors)) {
          newCompany.settings.projects.statusColors = []
        }
        await setCompany(newCompany);
        
        return newCompany;
      }catch(e) {
        await setCompany(undefined);
      }
    }

    if(company && !Array.isArray(company?.settings?.projects?.statusColors)) {
      company.settings.projects.statusColors = []
    }

    return company;
  }, []);

  const setCurrent = useCallback(async () => {
    try {
      const newCompany = await getCurrentCompany();
      setCompany(newCompany);
    }catch {
      setCompany(null);
    }
    
  }, []);

  const create = useCallback(async (company: any) => {
    const newCompany = await createCompany({...company, settings: defaultCompanySettings});
    setCompany(newCompany);
    return newCompany;
  }, []);

  const update = useCallback(async (company: Company) => {
    setCompany(await updateCompany(company))
    enqueueSnackbar('Company updated successfully', 
      { 
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' }
      });
  }, []);

  const getCurrentUsers = useCallback(async () => {
    let users = [];
    if(!company) {
      try {
        users = await getCurrentCompanyUsers();
      }catch(e) {
      }
    }

    return users;
  }, []);

  const addUser = useCallback(async (user: any) => {
    if(!user.password) user.password = v4();
    const newUser = await createUser(user);
    return newUser
  }, []);

  const addContract = useCallback(async (data: any) => {
    const company = await get()
    if(!company) return;

    const result =  await uploadContract({companyId: company.id, ...data})
    return result;
  }, []);

  const getContracts = useCallback(async () => {
    if(!company) {
      const company = await get();
      return (await getContractsApi(company.id) || []);
    };

    return (await getContractsApi(company.id) || []);

    
  }, []);

  const updateContract = useCallback(async (data: any) => {
    return await updateContractApi(data);
  }, []);

  const removeContract = useCallback(async (id: any) => {
    if(!company) return;

    await deleteContract({companyId: company.id, id})
  }, []);

  const addLogo = useCallback(async (data: any) => {
    const result =  await uploadLogo({...data})
    await setCurrent();
    return result;
  }, []);

  const getLogo = useCallback(async () => {
    await getLogoApi();
  }, []);

  const getSetupIntent = async () => {
    const res = await createSetupIntent();
    return res;
  }

  const getPaymentMethods = async () => {
    const res = await getPaymentMethodsAPI();
    return res;
  }

  const getInvoices = async () => {
    const res = await getInvoicesApi();
    return res;
  }

  const getNextInvoice = async () => {
    const res = await getCurrentInvoices();
    return res[0] || null;
  }

  const createOrUpdateSubscriptionPlan = async (planId: string) => {
    const result = await createOrUpdateSubscription(planId);
    await setCurrent(); // Refresh company data
    return result;
  }

  const cancelSubscriptionPlan = async () => {
    const result = await cancelSubscriptionApi();
    await setCurrent(); // Refresh company data
    return result;
  }

  useEffect(() => {
    get();
  }, []);

  const value = {
    company,
    set,
    get,
    setCurrent,
    create,
    update,
    getCurrentUsers,
    addUser,
    addContract,
    getContracts,
    updateContract,
    removeContract,
    addLogo,
    getLogo,
    getSetupIntent,
    getPaymentMethods,
    getInvoices,
    getNextInvoice,
    createOrUpdateSubscription: createOrUpdateSubscriptionPlan,
    cancelSubscription: cancelSubscriptionPlan
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = (): CompanyContextType => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};
